import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  TimelineSummary,
  LostCause,
} from "../../services/ShiftAnalysisService";
import { formatDuration } from "./utils";
import Panel from "./Panel";

interface Props {
  timeline: TimelineSummary | null;
  /** Stop causes (shift_class_agg) — richer int/ext downtime source. */
  causes: LostCause[];
  /** Line total downtime (ms) — fallback so the panel is never wrongly empty. */
  lineDowntimeMs: number;
}

const isInternal = (rating: string) => rating.toLowerCase().startsWith("int");

/**
 * "Downtime split" — internal (our own stops) vs external (starved/blocked/
 * setup). Prefers the aggregated stop-time from shift_class_agg (covers all
 * downtime, not just fault events); falls back to fault seconds. Severity and
 * the longest stop come from the fault timeline when available.
 */
const DowntimeSplit: React.FC<Props> = ({
  timeline,
  causes,
  lineDowntimeMs,
}) => {
  const { t } = useTranslation();

  const { internal, external, total, splitKnown } = useMemo(() => {
    if (causes.length > 0) {
      const internal =
        causes
          .filter((c) => isInternal(c.rating))
          .reduce((s, c) => s + c.total_stops_ms, 0) / 1000;
      const external =
        causes
          .filter((c) => !isInternal(c.rating))
          .reduce((s, c) => s + c.total_stops_ms, 0) / 1000;
      if (internal + external > 0) {
        return {
          internal,
          external,
          total: internal + external,
          splitKnown: true,
        };
      }
    }
    const internal = timeline?.internal_fault_seconds ?? 0;
    const external = timeline?.external_fault_seconds ?? 0;
    if (internal + external > 0) {
      return {
        internal,
        external,
        total: internal + external,
        splitKnown: true,
      };
    }
    // Last resort: we know downtime happened on the line but have no
    // int/ext breakdown for this machine (less-instrumented machines).
    const lineDown = Math.max(0, lineDowntimeMs) / 1000;
    return {
      internal: 0,
      external: 0,
      total: lineDown,
      splitKnown: false,
    };
  }, [causes, timeline, lineDowntimeMs]);

  const intPct = total > 0 && splitKnown ? (internal / total) * 100 : 0;
  const extPct = total > 0 && splitKnown ? (external / total) * 100 : 0;

  const hasFaults = !!timeline && timeline.total_faults > 0;
  const sev = [
    {
      key: "critical",
      label: t("critical", "Critical"),
      n: timeline?.critical_count ?? 0,
      color: "#ef4444",
    },
    {
      key: "major",
      label: t("major", "Major"),
      n: timeline?.major_count ?? 0,
      color: "#f59e0b",
    },
    {
      key: "minor",
      label: t("minor", "Minor"),
      n: timeline?.minor_count ?? 0,
      color: "#3b82f6",
    },
  ];

  return (
    <Panel
      label={t("downtimeSplit", "Downtime split")}
      meta={t("internalVsExternal", "internal vs external")}
    >
      {total <= 0 ? (
        <div
          className="flex flex-col items-center justify-center py-10 text-center"
          style={{ color: "var(--text-secondary)" }}
        >
          <span className="text-3xl mb-2">✅</span>
          <p className="text-sm">
            {t("noDowntimeShift", "No downtime recorded")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Total */}
          <div className="flex items-baseline gap-2">
            <span
              className="text-3xl font-extrabold tabular-nums tracking-tight"
              style={{ color: "var(--text-primary)" }}
            >
              {formatDuration(total)}
            </span>
            <span
              className="text-xs"
              style={{ color: "var(--text-secondary)" }}
            >
              {t("totalDowntime", "total downtime")}
            </span>
          </div>

          {/* Split bar */}
          {splitKnown ? (
            <div>
              <div
                className="flex h-3 w-full rounded-full overflow-hidden"
                style={{ backgroundColor: "var(--bg-secondary)" }}
              >
                <div
                  style={{ width: `${intPct}%`, backgroundColor: "#ef4444" }}
                  className="h-full transition-[width] duration-700"
                />
                <div
                  style={{ width: `${extPct}%`, backgroundColor: "#f59e0b" }}
                  className="h-full transition-[width] duration-700"
                />
              </div>
              <div className="flex justify-between mt-2 text-xs">
                <span className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: "#ef4444" }}
                  />
                  <span style={{ color: "var(--text-secondary)" }}>
                    {t("internal", "Internal")}
                  </span>
                  <span
                    className="font-semibold tabular-nums"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {formatDuration(internal)}
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    ({Math.round(intPct)}%)
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span style={{ color: "var(--text-secondary)" }}>
                    ({Math.round(extPct)}%)
                  </span>
                  <span
                    className="font-semibold tabular-nums"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {formatDuration(external)}
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {t("external", "External")}
                  </span>
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: "#f59e0b" }}
                  />
                </span>
              </div>
            </div>
          ) : (
            <div>
              <div
                className="h-3 w-full rounded-full"
                style={{ backgroundColor: "#94a3b8" }}
              />
              <p
                className="mt-2 text-xs"
                style={{ color: "var(--text-secondary)" }}
              >
                {t(
                  "downtimeSplitUnavailable",
                  "Internal/external breakdown not available for this machine.",
                )}
              </p>
            </div>
          )}

          {/* Severity + longest — only meaningful when fault events exist */}
          {hasFaults && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              {sev.map((s) => (
                <div
                  key={s.key}
                  className="rounded-xl px-3 py-2"
                  style={{ backgroundColor: "var(--bg-secondary)" }}
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: s.color }}
                    />
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {s.label}
                    </span>
                  </div>
                  <div
                    className="text-lg font-bold tabular-nums"
                    style={{ color: s.color }}
                  >
                    {s.n}
                  </div>
                </div>
              ))}
              <div
                className="rounded-xl px-3 py-2"
                style={{ backgroundColor: "var(--bg-secondary)" }}
              >
                <div
                  className="text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {t("longestStop", "Longest stop")}
                </div>
                <div
                  className="text-lg font-bold tabular-nums"
                  style={{ color: "var(--text-primary)" }}
                >
                  {formatDuration(timeline?.longest_fault_seconds ?? 0)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};

export default DowntimeSplit;
